import React, { useState } from "react";
import { CARD_BG, SHADOW } from "../config/theme";
import type { InsightCallout } from "../types";
import { useInsightPanelState } from "../hooks/useInsightPanelState";
import {
  getInsightTypeColor,
  getPriorityIcon,
  perspectiveConfigs,
} from "./insightsPanelConfig";

interface InsightsPanelProps {
  insights: InsightCallout[];
  onInsightsChange: (insights: InsightCallout[]) => void;
  onAddToCallouts?: (insight: InsightCallout) => void;
  onDismiss?: () => void;
  isLoading?: boolean;
  statusMessage?: string;
  generationTime?: number;
  modelUsed?: string;
  perspective?: "internal" | "external";
  title?: string;
  description?: string;
}

/**
 * Panel for displaying and editing AI-generated campaign insights.
 *
 * Features:
 * - Display insights with color-coded types
 * - Edit insight title and description
 * - Remove/accept individual insights
 * - Visual indicators for edited content
 * - Priority and confidence display
 */
export const InsightsPanel: React.FC<InsightsPanelProps> = ({
  insights,
  onInsightsChange,
  onAddToCallouts,
  onDismiss,
  isLoading = false,
  statusMessage,
  generationTime,
  modelUsed,
  perspective = "internal",
  title,
  description,
}) => {
  const {
    acceptedInsights,
    beginEdit,
    cancelEdit,
    editDescription,
    editTitle,
    editingId,
    removedInsights,
    saveEdit,
    setEditDescription,
    setEditTitle,
    updateInsightAcceptance,
  } = useInsightPanelState({ insights, onInsightsChange });

  if (isLoading) {
    return (
      <div
        className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-[#5B5353] p-6`}
      >
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <div className="text-gray-600 dark:text-[#B5A9AA]">
            <p>Generating AI insights...</p>
            {statusMessage && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {statusMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (acceptedInsights.length === 0 && insights.length === 0) {
    return null;
  }

  const config = perspectiveConfigs[perspective];

  return (
    <div
      className={`rounded-lg ${SHADOW.card} border ${config.borderColor} ${config.bgColor}`}
    >
      {/* Header */}
      <div className={`px-6 py-4 border-b ${config.borderColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{config.icon}</span>
            <div>
              <h3 className={`text-lg font-semibold ${config.titleColor}`}>
                {title || "AI-Powered Insights"}
              </h3>
              {description && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {description}
                </p>
              )}
              {modelUsed && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {modelUsed}
                  {generationTime && ` • ${generationTime.toFixed(1)}s`}
                </p>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {acceptedInsights.length} insight
            {acceptedInsights.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Insights List */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {acceptedInsights.map((insight, index) => (
          <div
            key={insight.id}
            className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            {editingId === insight.id ? (
              // Edit Mode
              <div className="space-y-3">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                           ${CARD_BG} text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Insight title"
                />
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                           ${CARD_BG} text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Insight description"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => saveEdit(insight.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                             transition-colors text-sm font-medium"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                             rounded-md hover:bg-gray-300 dark:hover:bg-gray-600
                             transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-2">
                {/* Header with badges */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    <span className="text-lg">
                      {getPriorityIcon(insight.priority)}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded border ${getInsightTypeColor(
                        insight.type,
                      )}`}
                    >
                      {insight.type.toUpperCase()}
                    </span>
                    {insight.is_edited && (
                      <span
                        className="px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300
                                     bg-purple-100 dark:bg-purple-900 rounded border border-purple-300 dark:border-purple-700"
                      >
                        EDITED
                      </span>
                    )}
                    {insight.metric_reference && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        📊 {insight.metric_reference}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-1 ml-4">
                    {onAddToCallouts && (
                      <button
                        onClick={() => onAddToCallouts(insight)}
                        className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400
                                 hover:text-blue-700 dark:hover:text-blue-300
                                 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40
                                 border border-blue-200 dark:border-blue-800
                                 rounded transition-colors"
                        title="Add to Key Callouts"
                      >
                        + Callout
                      </button>
                    )}
                    <button
                      onClick={() => beginEdit(insight)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400
                               transition-colors"
                      title="Edit insight"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() =>
                        updateInsightAcceptance(insight.id, false)
                      }
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400
                               transition-colors"
                      title="Remove insight"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Title */}
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  {insight.title}
                </h4>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {insight.description}
                </p>

                {/* Confidence */}
                <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>Confidence:</span>
                  <div className="flex-1 max-w-xs bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${insight.confidence * 100}%` }}
                    />
                  </div>
                  <span>{(insight.confidence * 100).toFixed(0)}%</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Removed Insights Section (collapsed) */}
      {removedInsights.length > 0 && (
        <details className="border-t border-gray-200 dark:border-gray-700">
          <summary
            className="px-6 py-3 cursor-pointer text-sm text-gray-500 dark:text-gray-400
                            hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            {removedInsights.length} removed insight
            {removedInsights.length !== 1 ? "s" : ""}
          </summary>
          <div className="divide-y divide-gray-200 dark:divide-gray-700 bg-gray-50 dark:bg-gray-900">
            {removedInsights.map((insight) => (
                <div
                  key={insight.id}
                  className="px-6 py-3 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-through">
                      {insight.title}
                    </p>
                  </div>
                  <button
                    onClick={() => updateInsightAcceptance(insight.id, true)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Restore
                  </button>
                </div>
              ))}
          </div>
        </details>
      )}
    </div>
  );
};
